import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { api, wsUrl } from '../lib/api';
import { useLang } from '../lib/i18n';
import { dayShort, hhmm, mmss, nf } from '../lib/format';
import { setState } from '../lib/store';
import { toast } from '../lib/toast';
import type { MapResponse } from '../lib/types';
import { ErrorView, Loading } from '../components/AppHeader';
import { Ico, Logo, Poster } from '../components/brand';

type Status =
  | { state: 'none' | 'expired' | 'done' }
  | { state: 'waiting'; position: number; ahead: number; eta: number; progress: number; joinedAt: number; waiting: number; soldOut: boolean; demo: boolean }
  | { state: 'admitted'; token: string; expiresAt: number; n: number };

export function Waiting() {
  const { sid = '' } = useParams();
  const nav = useNavigate();
  const { tx, lang } = useLang();
  const [info, setInfo] = useState<MapResponse | null>(null);
  const [taken, setTaken] = useState<number | null>(null);
  const [st, setSt] = useState<Status | null>(null);
  const [err, setErr] = useState(false);
  const [notify, setNotify] = useState(false);
  const [now, setNow] = useState(Date.now());
  const notifyRef = useRef(false);
  notifyRef.current = notify;

  useEffect(() => {
    api<MapResponse>(`/sessions/${sid}/map`).then(setInfo, () => setErr(true));
    api<{ sold: string[]; held: string[]; blocked: string[] }>(`/sessions/${sid}/state`).then((s) => setTaken(s.sold.length + s.held.length + s.blocked.length), () => {});
  }, [sid]);

  useEffect(() => {
    let ws: WebSocket | null = null;
    let poll: ReturnType<typeof setInterval> | undefined;
    let closed = false;
    const onStatus = (s: Status) => {
      setSt(s);
      if (s.state === 'admitted') {
        setState((x) => ({ ...x, admission: { ...x.admission, [sid]: { token: s.token, expiresAt: s.expiresAt } } }));
        if (notifyRef.current && document.hidden && 'Notification' in window && Notification.permission === 'granted') new Notification('Gişe', { body: lang === 'en' ? 'It’s your turn — pick your seats.' : 'Sıra sende — koltuğunu seç.' });
      }
    };
    api<Status>(`/queue/${sid}/join`, { method: 'POST' }).then(
      (s) => {
        onStatus(s);
        if (closed) return;
        ws = new WebSocket(wsUrl(`/queue/${sid}/live`));
        ws.onmessage = (e) => e.data !== 'pong' && onStatus(JSON.parse(e.data));
        ws.onclose = () => {
          if (closed) return;
          // fallback: polling (the server also suggests this for clients without WebSockets)
          poll = setInterval(() => api<Status>(`/queue/${sid}/status`).then(onStatus, () => {}), 3000);
        };
      },
      () => setErr(true),
    );
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      closed = true;
      ws?.close();
      clearInterval(poll);
      clearInterval(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sid]);

  if (err) return <ErrorView msg={tx('Bu seans için sıra yok.', 'There is no queue for this session.')} action={<button className="btn btn-line" onClick={() => nav('/')}>{tx('Keşfet’e dön', 'Back')}</button>} />;
  if (!info || !st) return <Loading />;
  const e = info.event;
  const capacity = info.map.seats.length;
  const left = taken === null ? null : capacity - taken;

  if (st.state === 'admitted')
    return (
      <div className="scroll">
        <div className="wr">
          <Logo size={30} />
          <span className="badge ok" style={{ marginTop: 28 }}>
            <Ico n="check" cls="ico-s" /> {tx('Sıra sende', 'It’s your turn')}
          </span>
          <h1 className="d-xl" style={{ margin: '14px 0 10px' }}>
            {tx('Koltuk seçme zamanı.', 'Time to pick your seats.')}
          </h1>
          <p className="t-l" style={{ margin: 0, color: 'var(--ink-2)' }}>
            {tx(`Koltuk haritasında 10 dakikan var. Kişi başı en fazla ${info.maxSeats} bilet alınabilir.`, `You have 10 minutes on the seat map. Up to ${info.maxSeats} tickets per person.`)}
          </p>
          <div className="panel" style={{ marginTop: 22, display: 'flex', alignItems: 'center', gap: 14 }}>
            <Ico n="clock" />
            <div style={{ flex: 1 }}>
              <div className="t-s muted">{tx('Seçim süren', 'Time left to choose')}</div>
              <div className="d-m mono">{mmss(st.expiresAt - now)}</div>
            </div>
          </div>
          <button type="button" className="btn btn-primary btn-lg btn-block" style={{ marginTop: 18 }} onClick={() => nav(`/koltuk/${sid}`)}>
            {tx('Koltuk seç', 'Select seats')}
          </button>
        </div>
      </div>
    );

  if (st.state !== 'waiting')
    return (
      <ErrorView
        msg={st.state === 'expired' ? tx('Seçim süren doldu.', 'Your selection window expired.') : tx('Bu sıradaki işlemin tamamlandı.', 'You’re done with this queue.')}
        action={
          <button type="button" className="btn btn-primary" onClick={() => location.reload()}>
            {tx('Sıraya yeniden gir', 'Rejoin the queue')}
          </button>
        }
      />
    );

  const joined = new Date(st.joinedAt).toLocaleTimeString(lang === 'en' ? 'en-GB' : 'tr-TR', { timeZone: 'Europe/Istanbul' });
  const toggleNotify = async (on: boolean) => {
    if (on && 'Notification' in window && Notification.permission === 'default') await Notification.requestPermission();
    const ok = on && 'Notification' in window && Notification.permission === 'granted';
    setNotify(ok);
    toast(ok ? tx('Sıra gelince bu tarayıcıda bildirim göstereceğiz', 'We’ll notify you in this browser when it’s your turn') : on ? tx('Bildirim izni verilmedi', 'Notifications are blocked') : tx('Bildirim kapatıldı', 'Notifications off'));
  };

  return (
    <div className="scroll">
      <div className="wr">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Logo size={30} />
          <span className="badge">
            <span className="pulse" /> {tx('Sıra canlı', 'Queue is live')}
          </span>
        </div>
        <div className="mini-ev" style={{ marginTop: 24, gridTemplateColumns: '56px 1fr' }}>
          <Poster poster={e.poster} title={e.short || e.title} />
          <div>
            <div className="cap muted">
              {tx('Satış açık · gösteri', 'On sale · show')} {dayShort(info.session.startsAt, lang)} {hhmm(info.session.startsAt)}
            </div>
            <div className="d-s">{e.title}</div>
            <div className="t-s muted">
              {info.venue.name}, {info.venue.area}
            </div>
          </div>
        </div>
        <p className="cap muted" style={{ margin: '34px 0 6px' }}>
          {tx('Sıradaki yerin', 'Your place in line')}
        </p>
        <div className="pos" aria-live="polite">
          {nf(st.position, lang)}
        </div>
        <p className="t-l" style={{ margin: '10px 0 18px' }}>
          {lang === 'en' ? (
            <>
              <b>{nf(st.ahead, 'en')}</b> people ahead of you. Estimated wait <b>{st.eta} min</b>.
            </>
          ) : (
            <>
              Önünde <b>{nf(st.ahead)}</b> kişi var. Tahmini bekleme <b>{st.eta} dk</b>.
            </>
          )}
        </p>
        <div className="prog" role="progressbar" aria-label={tx('Sıra ilerlemesi', 'Queue progress')} aria-valuenow={Math.round(st.progress * 100)} aria-valuemin={0} aria-valuemax={100}>
          <i style={{ width: `${st.progress * 100}%` }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }} className="t-s muted">
          <span>
            {tx('Sıraya girdin', 'You joined at')} {joined}
          </span>
          <span>
            {capacity} {tx('koltuk', 'seats')}
            {left !== null ? ` · ${left} ${tx('boş', 'free')}` : ''}
          </span>
        </div>
        {st.soldOut && (
          <div className="err-banner" style={{ marginTop: 16 }}>
            <Ico n="info" cls="ico-s" /> {tx('Biletler tükendi. İptal olursa sıradakilere açılacak.', 'Sold out. Returned tickets go to the queue in order.')}
          </div>
        )}
        <div className="panel" style={{ marginTop: 24, display: 'flex', gap: 14, alignItems: 'flex-start' }}>
          <Ico n="info" />
          <div>
            <b>{tx('Bu sayfayı yenilemene gerek yok.', 'No need to refresh this page.')}</b>
            <p className="t-s muted" style={{ margin: '4px 0 0' }}>
              {tx(
                'Yerin bu cihazda saklı. Sekmeyi kapatıp geri dönersen yerini kaybetmezsin. Yenilemek sırayı hızlandırmaz.',
                'Your place is kept on this device. Close the tab and come back — you won’t lose it. Refreshing doesn’t move you forward.',
              )}
            </p>
          </div>
        </div>
        <div className="panel" style={{ marginTop: 12, display: 'flex', gap: 14, alignItems: 'center' }}>
          <Ico n="bell" />
          <label htmlFor="nt" style={{ flex: 1 }}>
            <b>{tx('Sıra gelince haber ver', 'Tell me when it’s my turn')}</b>
            <span className="t-s muted" style={{ display: 'block' }}>
              {tx('Tarayıcı bildirimi gönderelim, başka işine bak.', 'We’ll send a browser notification so you can do something else.')}
            </span>
          </label>
          <span className="switch">
            <input id="nt" type="checkbox" checked={notify} onChange={(ev) => toggleNotify(ev.target.checked)} />
            <i />
          </span>
        </div>
        <div className="sec-h">
          <h2 className="d-s">{tx('Sırada ne var?', 'What happens next?')}</h2>
        </div>
        <ol className="wsteps">
          <li className="now">
            <span className="k">1</span>
            <div>
              <b>{tx('Sıra', 'Queue')}</b>
              <span>{tx('Satış saatinden önce gelen herkes rastgele sıralandı; erken gelen avantaj sağlamadı.', 'Everyone who arrived before sales opened was shuffled; arriving early gave no advantage.')}</span>
            </div>
          </li>
          <li>
            <span className="k">2</span>
            <div>
              <b>{tx('Koltuk seçimi · 10 dk', 'Seat selection · 10 min')}</b>
              <span>{tx(`Sıran gelince haritaya geçersin. Kişi başı en fazla ${info.maxSeats} bilet.`, `When it’s your turn you go to the map. Up to ${info.maxSeats} tickets each.`)}</span>
            </div>
          </li>
          <li>
            <span className="k">3</span>
            <div>
              <b>{tx('Ödeme · 7 dk', 'Payment · 7 min')}</b>
              <span>{tx('Seçtiğin koltuklar ödeme boyunca yalnızca senin için tutulur.', 'Your seats are held only for you while you pay.')}</span>
            </div>
          </li>
        </ol>
        <p className="t-s muted" style={{ margin: '18px 0 0' }}>
          {tx('Biletler tükenirse seni hemen haberdar ederiz ve iptal edilen biletler için bekleme listesine ekleriz.', 'If tickets sell out we’ll tell you right away and keep you on the list for returns.')}
        </p>
        {st.demo && (
          <div style={{ marginTop: 14, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() =>
                api<Status>(`/queue/${sid}/skip`, { method: 'POST' }).then((s) => {
                  if (s.state === 'admitted') setState((x) => ({ ...x, admission: { ...x.admission, [sid]: { token: s.token, expiresAt: s.expiresAt } } }));
                  setSt(s);
                })
              }>
              {tx('Demo: sırayı atla', 'Demo: skip the queue')}
            </button>
            <span className="t-s muted">{tx('Demo modunda önünde sanal bir kalabalık var.', 'In demo mode a virtual crowd is queued ahead of you.')}</span>
          </div>
        )}
      </div>
    </div>
  );
}
